
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar";
import "./CityWiseLead.css";
import API_BASE_URL from "./.././../config/env";

interface CityStats {
  id: number;
  city: string;
  zone: string;
  total: number;
  active: number;
  invalid: number;
  not_q: number;
  site_visit: number;
  f2f: number;
  scheduled: number;
  token_done: number;
  transactions: number;
}

interface CardsData {
  user_count: number;
  total_lead: number;
  scheduled: number;
  f2f_count: number;
  site_visit_count: number;
  token_done_count: number;
  transaction_done_count: number;
  invalid_count: number;
}

interface FiltersData {
  from_date: string | null;
  to_date: string | null;
}

interface ApiResponse {
  status: string;
  filters: FiltersData;
  cards: CardsData;
  city_wise_data: CityStats[];
}

interface GPCUser {
  id: number;
  name: string;
  email: string;
  mobile_number: string | null;
  role_id: number;
  reporting_manager_id: number;
}

const CityWiseLead: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPartner, setSelectedPartner] = useState<string>("");
  const [selectedReportingUser, setSelectedReportingUser] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [reportingSearchTerm, setReportingSearchTerm] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isReportingDropdownOpen, setIsReportingDropdownOpen] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [gpcUsers, setGpcUsers] = useState<GPCUser[]>([]);
  const [reportingUsers, setReportingUsers] = useState<GPCUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingReportingUsers, setLoadingReportingUsers] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const reportingDropdownRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem("authToken");

  // Date filter states
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  useEffect(() => {
    // Set default dates (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setToDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    fetchGPCUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close reporting dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reportingDropdownRef.current && !reportingDropdownRef.current.contains(event.target as Node)) {
        setIsReportingDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch city-wise data with user_id parameter and date filters
  const fetchCityWiseData = async (userId: string, fromDateParam?: string, toDateParam?: string) => {
    if (!userId) {
      console.warn("No user ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    // Build URL with query parameters
    let url = `${API_BASE_URL}/api/partner-count/leads/city-wise-data/${userId}`;
    const params = new URLSearchParams();
    
    // Add date filters if provided
    if (fromDateParam) {
      params.append('from_date', fromDateParam);
    }
    if (toDateParam) {
      params.append('to_date', toDateParam);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    try {
      console.log("Fetching city-wise data from:", url);

      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          timeout: 30000,
        }
      );

      console.log("API Response:", response.data);

      if (response.data.status === "success") {
        setApiData(response.data);
      } else {
        setError(response.data.message || "Failed to fetch data");
      }
    } catch (err: any) {
      console.error("Error fetching city-wise data:", err);
      
      let errorMessage = "Failed to fetch city-wise data";
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = `API endpoint not found. Please check the URL: ${url}`;
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to access this data.";
        } else {
          errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchGPCUsers = async () => {
    if (!token) {
      console.warn("No token found for fetching GPC users");
      setError("Authentication token not found. Please login again.");
      setLoading(false);
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/partner-count/gpc-users-over-target`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          timeout: 15000,
        }
      );

      console.log("GPC Users Response:", response.data);

      if (response.data.status === "success" && response.data.data) {
        setGpcUsers(response.data.data);
        if (response.data.data.length > 0) {
          // Set default user - either 1305 or first user
          const defaultUser = response.data.data.find((user: GPCUser) => user.id === 1305) || response.data.data[0];
          const defaultUserId = String(defaultUser.id);
          setSelectedPartner(defaultUserId);
          setSearchTerm(defaultUser.name);
          
          // Fetch reporting users for the selected user
          await fetchReportingUsers(defaultUserId);
        } else {
          setError("No users found");
          setLoading(false);
        }
      } else {
        console.warn("Failed to fetch GPC users:", response.data.message);
        setError(response.data.message || "Failed to fetch users");
        setLoading(false);
      }
    } catch (err: any) {
      console.error("Error fetching GPC users:", err);
      
      let errorMessage = "Failed to fetch users";
      if (err.response) {
        errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
        if (err.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      }
      setError(errorMessage);
      setLoading(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch reporting users with proper CORS handling
  const fetchReportingUsers = async (userId: string) => {
    if (!userId) {
      console.warn("No user ID provided for fetching reporting users");
      setReportingUsers([]);
      setSelectedReportingUser("");
      setReportingSearchTerm("");
      fetchCityWiseData(userId, fromDate, toDate);
      return;
    }

    console.log("Fetching reporting users for targetUserId:", userId);
    setLoadingReportingUsers(true);
    
    try {
      // Remove trailing slash before query parameter to avoid CORS redirect issue
      const url = `${API_BASE_URL}/api/partner-count/gpc-users-over-target?targetUserId=${userId}`;
      console.log("Fetching reporting users from:", url);

      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          timeout: 15000,
        }
      );

      console.log("Reporting Users Response Data:", response.data);

      if (response.data.status === "success" && response.data.data && response.data.data.length > 0) {
        console.log(`Found ${response.data.data.length} reporting users`);
        console.log("Reporting Users List:", response.data.data);
        
        // Set the reporting users state
        setReportingUsers(response.data.data);
        
        // Set default reporting user - first user
        const defaultReportingUser = response.data.data[0];
        setSelectedReportingUser(String(defaultReportingUser.id));
        setReportingSearchTerm(defaultReportingUser.name);
        
        // Fetch city-wise data with the selected reporting user
        fetchCityWiseData(String(defaultReportingUser.id), fromDate, toDate);
      } else {
        console.log("No reporting users found for this partner");
        setReportingUsers([]);
        setSelectedReportingUser("");
        setReportingSearchTerm("");
        // If no reporting users, fetch data with the parent user
        fetchCityWiseData(userId, fromDate, toDate);
      }
    } catch (err: any) {
      console.error("Error fetching reporting users:", err);
      
      let errorMessage = "Failed to fetch reporting users";
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = "API endpoint not found. Please check the URL.";
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to access this data.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = err.response.data?.message || errorMessage;
        }
      } else if (err.message && err.message.includes('CORS')) {
        errorMessage = "CORS error: Please contact administrator to enable CORS for this API.";
      } else if (err.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      setReportingUsers([]);
      setSelectedReportingUser("");
      setReportingSearchTerm("");
      // If error, fetch data with the parent user
      fetchCityWiseData(userId, fromDate, toDate);
    } finally {
      setLoadingReportingUsers(false);
    }
  };

  // Handle partner selection
  const handlePartnerSelect = (userId: string, userName: string) => {
    console.log("Partner selected with ID:", userId, "Name:", userName);
    setSelectedPartner(userId);
    setSearchTerm(userName);
    setIsDropdownOpen(false);
    
    // Reset reporting users
    setReportingUsers([]);
    setSelectedReportingUser("");
    setReportingSearchTerm("");
    
    // Fetch reporting users for the selected partner
    fetchReportingUsers(userId);
  };

  // Handle reporting user selection
  const handleReportingUserSelect = (userId: string, userName: string) => {
    console.log("Reporting user selected:", userId, userName);
    setSelectedReportingUser(userId);
    setReportingSearchTerm(userName);
    setIsReportingDropdownOpen(false);
    // Fetch city-wise data with the selected reporting user
    fetchCityWiseData(userId, fromDate, toDate);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  // Handle reporting search input change
  const handleReportingSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReportingSearchTerm(e.target.value);
    setIsReportingDropdownOpen(true);
  };

  // Handle date filter submit
  const handleDateFilterSubmit = () => {
    const userId = selectedReportingUser || selectedPartner;
    if (userId) {
      fetchCityWiseData(userId, fromDate, toDate);
    }
  };

  // Filter users based on search term
  const getFilteredUsers = () => {
    if (!searchTerm.trim()) {
      return gpcUsers;
    }
    return gpcUsers.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Filter reporting users based on search term
  const getFilteredReportingUsers = () => {
    if (!reportingSearchTerm.trim()) {
      return reportingUsers;
    }
    return reportingUsers.filter(user =>
      user.name.toLowerCase().includes(reportingSearchTerm.toLowerCase())
    );
  };

  const getStatusCards = () => {
    if (!apiData || !apiData.cards) return [];
    
    const cards = apiData.cards;
    return [
      { label: "Total Members", value: cards.user_count || 0, color: "#4A90D9" },
      { label: "Total Leads", value: cards.total_lead || 0, color: "#27AE60" },
      { label: "Scheduled", value: cards.scheduled || 0, color: "#F39C12" },
      { label: "F2F Done", value: cards.f2f_count || 0, color: "#8E44AD" },
      { label: "Site Visit", value: cards.site_visit_count || 0, color: "#1ABC9C" },
      { label: "Token Done", value: cards.token_done_count || 0, color: "#E67E22" },
      { label: "Transactions", value: cards.transaction_done_count || 0, color: "#2ECC71" },
      { label: "Invalid", value: cards.invalid_count || 0, color: "#E74C3C" },
    ];
  };

  const getCityData = () => {
    if (!apiData) return [];
    return apiData.city_wise_data || [];
  };

  const getValueColor = (value: number) => {
    if (value >= 25) return "#E74C3C";
    if (value >= 18) return "#F39C12";
    if (value >= 10) return "#3498DB";
    if (value >= 5) return "#27AE60";
    return "#95A5A6";
  };

  const getRowColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#E8F5E9',
      'Mumbai': '#FFF3E0',
      'Bengaluru': '#E3F2FD',
      'Dubai': '#FCE4EC',
      'Global Partners Connect': '#F3E5F5',
      'Global Property Expo': '#E0F7FA',
      'Propex': '#FFF8E1',
      'Home 365': '#F3E5F5',
    };
    return colors[city] || '#FFFFFF';
  };

  const getRowHoverColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#C8E6C9',
      'Mumbai': '#FFE0B2',
      'Bengaluru': '#BBDEFB',
      'Dubai': '#F8BBD0',
      'Global Partners Connect': '#E1BEE7',
      'Global Property Expo': '#B2EBF2',
      'Propex': '#FFECB3',
      'Home 365': '#E1BEE7',
    };
    return colors[city] || '#F5F5F5';
  };

  const getRowBorderColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#4CAF50',
      'Mumbai': '#FF9800',
      'Bengaluru': '#2196F3',
      'Dubai': '#E91E63',
      'Global Partners Connect': '#9C27B0',
      'Global Property Expo': '#00BCD4',
      'Propex': '#FFC107',
      'Home 365': '#9C27B0',
    };
    return colors[city] || '#DDDDDD';
  };

  // Handle city name click to navigate to lead details
  const handleCityClick = (cityName: string) => {
    const userId = selectedReportingUser || selectedPartner;
    if (userId) {
      navigate(`/admin-leads/${userId}?city=${encodeURIComponent(cityName)}`);
    }
  };

  // Handle value circle click
  const handleValueClick = (cityName: string, type: string) => {
    const userId = selectedReportingUser || selectedPartner;
    if (userId) {
      navigate(`/admin-leads/${userId}?type=${type}&city=${encodeURIComponent(cityName)}`);
    }
  };

  const retryFetch = () => {
    fetchGPCUsers();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const isPageLoading = loadingUsers || loading || loadingReportingUsers;

  return (
    <div className="city-wise-lead-page">
      <Sidebar  />
      
      <div className={`city-wise-lead-container ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="header-section">
          <h2>City-wise Leads Overview</h2>
          <p className="subtitle">Monitor leads by city and location</p>
        </div>

        {/* Filter Section with Searchable Dropdown and Date Filter */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-col">
              <label className="filter-label">Select Partner</label>
              <div className="searchable-dropdown" ref={dropdownRef}>
                <div className="dropdown-input-wrapper">
                  <input
                    type="text"
                    placeholder="Search and select partner..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="search-input"
                    disabled={loadingUsers || loading}
                  />
                  <span className="dropdown-arrow" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    {isDropdownOpen ? '▲' : '▼'}
                  </span>
                </div>
                {isDropdownOpen && (
                  <div className="dropdown-list">
                    {loadingUsers ? (
                      <div className="dropdown-loading">Loading partners...</div>
                    ) : getFilteredUsers().length > 0 ? (
                      getFilteredUsers().map((user) => (
                        <div
                          key={user.id}
                          className={`dropdown-item ${selectedPartner === String(user.id) ? 'selected' : ''}`}
                          onClick={() => handlePartnerSelect(String(user.id), user.name)}
                        >
                          <span className="user-name">{user.name}</span>
                          {selectedPartner === String(user.id) && (
                            <span className="check-mark">✓</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-no-results">No partners found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Reporting Users Dropdown - Show when partner is selected */}
            {selectedPartner && (
              <div className="filter-col">
                <label className="filter-label">Select Reporting User</label>
                {loadingReportingUsers ? (
                  <div className="loading-reporting-users">
                    <span className="loading-text">Loading reporting users...</span>
                  </div>
                ) : reportingUsers.length > 0 ? (
                  <div className="searchable-dropdown" ref={reportingDropdownRef}>
                    <div className="dropdown-input-wrapper">
                      <input
                        type="text"
                        placeholder="Search reporting users..."
                        value={reportingSearchTerm}
                        onChange={handleReportingSearchChange}
                        onFocus={() => {
                          if (reportingUsers.length > 0) {
                            setIsReportingDropdownOpen(true);
                          }
                        }}
                        className="search-input"
                        disabled={loadingReportingUsers || loading}
                      />
                      <span className="dropdown-arrow" onClick={() => setIsReportingDropdownOpen(!isReportingDropdownOpen)}>
                        {isReportingDropdownOpen ? '▲' : '▼'}
                      </span>
                    </div>
                    {isReportingDropdownOpen && (
                      <div className="dropdown-list">
                        {getFilteredReportingUsers().length > 0 ? (
                          getFilteredReportingUsers().map((user) => (
                            <div
                              key={user.id}
                              className={`dropdown-item ${selectedReportingUser === String(user.id) ? 'selected' : ''}`}
                              onClick={() => handleReportingUserSelect(String(user.id), user.name)}
                            >
                              <span className="user-name">{user.name}</span>
                              {selectedReportingUser === String(user.id) && (
                                <span className="check-mark">✓</span>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="dropdown-no-results">No reporting users found</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-reporting-users">
                    <span className="no-data-text">No reporting users available</span>
                  </div>
                )}
              </div>
            )}

            {/* Date Filter Column */}
            <div className="filter-col date-filter-col">
              <label className="filter-label">Date Range</label>
              <div className="date-filter-container">
                <div className="date-input-group">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="date-input"
                    max={toDate}
                  />
                  <span className="date-separator">-</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="date-input"
                    min={fromDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <button
                  onClick={handleDateFilterSubmit}
                  className="date-submit-btn"
                  disabled={loading}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isPageLoading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{loadingUsers ? "Loading partners..." : loadingReportingUsers ? "Loading reporting users..." : "Loading city-wise data..."}</p>
          </div>
        )}

        {/* Error State */}
        {error && !isPageLoading && (
          <div className="error-state">
            <span className="error-icon">⚠️</span>
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={retryFetch} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Data Display */}
        {!isPageLoading && !error && apiData && (
          <>
            <div className="summary-section">
              <h3>Overall Status Summary</h3>
              <div className="status-cards">
                {getStatusCards().map((card, index) => (
                  <div
                    key={index}
                    className="status-card"
                    style={{ borderLeftColor: card.color }}
                  >
                    <span className="status-value">{card.value}</span>
                    <span className="status-label">{card.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="table-section">
              <h3>City-wise Leads Distribution</h3>
              <div className="table-wrapper">
                <table className="city-table">
                  <thead>
                    <tr>
                      <th>CITY</th>
                      <th>TOTAL</th>
                      <th>ACTIVE</th>
                      <th>INVALID</th>
                      <th>NOT Q</th>
                      <th>SITE V</th>
                      <th>F2F</th>
                      <th>SCHED</th>
                      <th>TOKEN</th>
                      <th>TRANS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCityData().map((city, index) => (
                      <tr 
                        key={index}
                        className="city-row"
                        style={{
                          backgroundColor: getRowColor(city.city),
                          borderLeft: `4px solid ${getRowBorderColor(city.city)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = getRowHoverColor(city.city);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = getRowColor(city.city);
                        }}
                      >
                        <td 
                          className="city-name clickable"
                          onClick={() => handleCityClick(city.city)}
                        >
                          <span>{city.city}</span>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.total) }}
                            onClick={() => handleValueClick(city.city, 'total')}
                          >
                            {city.total}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.active) }}
                            onClick={() => handleValueClick(city.city, 'active')}
                          >
                            {city.active}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.invalid) }}
                            onClick={() => handleValueClick(city.city, 'invalid')}
                          >
                            {city.invalid}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.not_q) }}
                            onClick={() => handleValueClick(city.city, 'not_q')}
                          >
                            {city.not_q}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.site_visit) }}
                            onClick={() => handleValueClick(city.city, 'site_visit')}
                          >
                            {city.site_visit}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.f2f) }}
                            onClick={() => handleValueClick(city.city, 'f2f')}
                          >
                            {city.f2f}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.scheduled) }}
                            onClick={() => handleValueClick(city.city, 'scheduled')}
                          >
                            {city.scheduled}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.token_done) }}
                            onClick={() => handleValueClick(city.city, 'token_done')}
                          >
                            {city.token_done}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.transactions) }}
                            onClick={() => handleValueClick(city.city, 'transaction')}
                          >
                            {city.transactions}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {getCityData().length === 0 && (
                <div className="no-data">
                  <p>No city-wise data available</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CityWiseLead;